import { Vector3 } from '@kitware/vtk.js/types';
import vtkCoordinate from '@kitware/vtk.js/Rendering/Core/Coordinate';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import { configStore } from 'config/configStore';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import { SurfaceContext } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { getOrCreateManualVolume } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import { runWorkerTask } from '@features/analysis/rendering/Panels/workerPool';
import {
  applyScreenPolygonEditState,
  filterActiveMaskLinearIndices,
} from '@features/analysis/segmentManager/core/SegmentManagerCore';

export const EDIT_WARNING_MESSAGE =
  'If you change the mode, any unsaved changes from your previous edits may be lost.\nDo you want to continue?';

export type EditModeAction = 'Eraser' | 'commit' | 'coron' | 'dilate' | 'erode';

export function handleEditModeAction(
  mode: EditModeAction,
  selectedPanelComponentKey?: string,
) {
  const {EditMode, manualLabelmapStyle} = configStore.get();
  if (selectedPanelComponentKey === 'Surface3D' && EditMode) {
    scheduleRender('Surface3D', {
      type: 'FFR_Manual_3D',
      mode,
    });
  }else if(mode === 'coron' && EditMode){
    scheduleRender('Surface3D', {
      type: 'FFR_Manual_3D',
      mode,
      manualLabelmapStyle
    });
  }
}

export function handleBrushSize(
  delta: number
){
  scheduleRender('Axial', { type: 'uknowuknow', brushStepDelta: delta });
  scheduleRender('Coronal', { type: 'uknowuknow', brushStepDelta: delta });
  scheduleRender('Sagittal', { type: 'uknowuknow', brushStepDelta: delta });
}


export function getWorldUnitsPerPixel(renderer: any) {
  if (!renderer) return 1;
  const camera = renderer.getActiveCamera();
  const [, h] = renderer.getRenderWindow().getViews()[0].getSize(); // getOpenGLRenderWindow().getSize()? ?숈씪 怨꾩뿴
  const isParallel = camera.getParallelProjection();

  if (isParallel) {
    const scale = camera.getParallelScale();
    return (2 * scale) / h;
  } else {
    const distance = camera.getDistance();
    const angle = camera.getViewAngle(); // degrees
    return (2 * distance * Math.tan((angle * Math.PI) / 360)) / h;
  }
}

export function add(a: Vector3, b: Vector3): Vector3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function sub(a: Vector3, b: Vector3): Vector3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function mul(v: Vector3, s: number): Vector3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

export function rayFromDisplay(
  displayCoord: vtkCoordinate,
  renderer: vtkRenderer,
  x: number,
  y: number,
) {
  // near
  displayCoord.setValue([x, y, 0]);
  const near = displayCoord.getComputedWorldValue(renderer) as Vector3;

  // far
  displayCoord.setValue([x, y, 1]);

  const far = displayCoord.getComputedWorldValue(renderer) as Vector3;

  const dir: Vector3 = [far[0] - near[0], far[1] - near[1], far[2] - near[2]];

  return {
    origin: near,
    target: far,
    dir,
  };
}

export function intersectRayAABB(p0: Vector3, p1: Vector3, b: number[]) {
  const dir: Vector3 = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]];
  let tmin = 0,
    tmax = 1;
  for (let ax = 0; ax < 3; ax++) {
    const invD = 1 / dir[ax];
    let t1 = (b[ax * 2] - p0[ax]) * invD; // min
    let t2 = (b[ax * 2 + 1] - p0[ax]) * invD; // max
    if (invD < 0) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
    }
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmax < tmin) return null;
  }
  return [tmin, tmax] as [number, number];
}

export function worldToContinuousIndex(
  world: Vector3,
  origin: Vector3,
  spacing: Vector3,
): Vector3 {
  return [
    (world[0] + origin[0]) / spacing[0],
    (origin[1] - world[1]) / spacing[1],
    (world[2] - origin[2]) / spacing[2],
  ];
}

export async function polyDataFromScreenPolygon(
  context: SurfaceContext,
  seriesId: string | undefined,
  pts: Float32Array | number[],
  lowQuality: boolean,
  isSelectedArea = true,
) {
  console.time('polydataFromScreenPolygon');
  const result = await getScreenPolygonLinearIndices(
    context,
    seriesId,
    pts,
    lowQuality,
    isSelectedArea,
  );
  console.timeEnd('polydataFromScreenPolygon');

  if (!result) {
    return;
  }

  applyScreenPolygonLinearIndices(context, result.linearIndices, 0);
}

export async function getScreenPolygonLinearIndices(
  context: SurfaceContext,
  seriesId: string | undefined,
  pts: Float32Array | number[],
  lowQuality: boolean,
  isSelectedArea = true,
): Promise<{
  linearIndices: Uint32Array;
  dimensions: [number, number, number];
} | null> {
  if (!context || pts.length === 0 || !seriesId) {
    return null;
  }

  const { renderer } = context;

  // segmentation volume
  const volume = getOrCreateManualVolume(seriesId, lowQuality);

  if (!volume) return null;

  const imageData = volume.imageData ?? (volume as any).getImageData?.();

  if (!imageData) return null;

  const rawDimensions = imageData.getDimensions();
  const dimensions: [number, number, number] = [
    rawDimensions[0],
    rawDimensions[1],
    rawDimensions[2],
  ];

  const spacing = imageData.getSpacing();

  const origin = imageData.getOrigin();

  const displayCoord = vtkCoordinate.newInstance();

  displayCoord.setCoordinateSystemToDisplay();

  const rays = new Float32Array((pts.length / 2) * 6);
  let rayPtr = 0;

  for (let idx = 0; idx < pts.length; idx += 2) {
    const x = pts[idx];
    const y = pts[idx + 1];

    const ray = rayFromDisplay(displayCoord, renderer, x, y);
    rays[rayPtr++] = ray.origin[0];
    rays[rayPtr++] = ray.origin[1];
    rays[rayPtr++] = ray.origin[2];
    rays[rayPtr++] = ray.target[0];
    rays[rayPtr++] = ray.target[1];
    rays[rayPtr++] = ray.target[2];
  }

  const { linearIndices } = await runWorkerTask({
    type: 'screenPolygonLinearIndices',
    rays,
    dimensions,
    spacing,
    origin,
    isSelectedArea,
  }, [rays.buffer as ArrayBuffer]);

  return {
    linearIndices,
    dimensions,
  };
}

export function applyScreenPolygonLinearIndices(
  context: SurfaceContext,
  linearIndices: Uint32Array,
  segmentIndex = 0,
) {
  return applyScreenPolygonEditState(linearIndices, context, segmentIndex);
}

export function filterActiveMaskScreenPolygonLinearIndices(
  context: SurfaceContext,
  linearIndices: Uint32Array,
) {
  return filterActiveMaskLinearIndices(linearIndices, context);
}
